import { UiStringsProvider, type UiStringsInput } from "@seagames/ui";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { intlLocaleFor } from "./locales";

/**
 * Feeds `@seagames/ui` its chrome (table, pagination, select, filter bar,
 * modal, list toolbar) from the `common` namespace — once, for the whole app, so no list
 * page has to remember to pass "Filter" / "Rows per page" / "Search…" itself.
 *
 * `useTranslation` re-renders this on a language change; the object is
 * memoised on the language so the tree below re-renders only then. Numbers go
 * through `intlLocaleFor()` — "bm" must never reach Intl (it is Bambara there).
 */
export function AppUiStrings({ children }: { children: ReactNode }) {
	const { t, i18n } = useTranslation("common");
	const language = i18n.language;

	const value = useMemo<UiStringsInput>(() => {
		const nf = new Intl.NumberFormat(intlLocaleFor(language));
		const fmt = (n: number) => nf.format(n);
		return {
			formatNumber: fmt,
			dataTable: {
				actionHeader: t("dataTable.action"),
				errorTitle: t("dataTable.errorTitle"),
				retry: t("dataTable.tryAgain"),
				emptyTitle: t("dataTable.emptyTitle"),
				selected: t("dataTable.selected"),
				onOtherPages: (count) => t("dataTable.onOtherPages", { n: fmt(count) }),
				clearSelection: t("action.clear"),
				columns: t("dataTable.columns"),
				showColumns: t("dataTable.showColumns"),
				columnAlways: t("dataTable.always"),
				showAllColumns: t("dataTable.showAll"),
				hiddenCount: (count) => t("dataTable.hiddenCount", { n: fmt(count) }),
				selectRow: (row) => t("dataTable.selectRow", { row: fmt(row) }),
				selectAllRows: t("dataTable.selectAllRows"),
				deselectAllRows: t("dataTable.deselectAllRows"),
			},
			pagination: {
				label: t("pagination.label"),
				summary: ({ from, to, total }) =>
					t("pagination.summary", { count: total, from: fmt(from), to: fmt(to), total: fmt(total) }),
				empty: t("pagination.empty"),
				rowsPerPage: t("pagination.rowsPerPage"),
				firstPage: t("pagination.firstPage"),
				previousPage: t("pagination.previousPage"),
				nextPage: t("pagination.nextPage"),
				lastPage: t("pagination.lastPage"),
				page: (page) => t("pagination.page", { page: fmt(page) }),
				summaryWithNoun: ({ from, to, total, noun }) =>
					t("pagination.summaryWithNoun", { from: fmt(from), to: fmt(to), total: fmt(total), noun }),
				emptyWithNoun: (noun) => t("pagination.emptyWithNoun", { noun }),
				showing: ({ from, to, total }) =>
					t("pagination.showing", { count: total, from: fmt(from), to: fmt(to), total: fmt(total) }),
				perPage: (n) => t("pagination.perPage", { n: fmt(n) }),
				showingNoTotal: ({ from, to }) => t("pagination.showingNoTotal", { from: fmt(from), to: fmt(to) }),
			},
			select: {
				placeholder: t("action.select"),
				searchPlaceholder: t("action.search"),
				noMatches: t("state.noMatches"),
				clear: t("action.clear"),
				removeOption: (label) => t("select.removeOption", { label }),
				selectedCount: (count) => t("select.selectedCount", { n: fmt(count) }),
				selectAll: t("select.selectAll"),
			},
			filterBar: { label: t("filterBar.label"), apply: t("action.filter"), reset: t("action.reset") },
			modal: { close: t("action.close") },
			list: {
				searchPlaceholder: t("list.searchPlaceholder"),
				search: t("list.search"),
				clearSearch: t("list.clearSearch"),
				sort: t("list.sort"),
				clearAll: t("list.clearAll"),
				quickFilters: t("list.quickFilters"),
			},
		};
	}, [t, language]);

	return <UiStringsProvider value={value}>{children}</UiStringsProvider>;
}
